<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { Download, RotateCcw, Search } from '@lucide/vue'
import type { ReviewAction, ReviewLog } from './types'
import {
  MACHINE_TAG_OPTIONS,
  REVIEW_ACTION_OPTIONS,
  machineTagLabel,
  reviewActionLabel
} from './types'
import { highlightSegments, machineTagTone } from './format'
import { getLog, listLogs, type LogFilter } from './mock'
import { usePaged } from './usePaged'

import {
  AdminDataTable,
  AdminFilterBar,
  AdminDrawer,
  AdminStatusTag,
  RrInput,
  RrSelect,
  RrButton,
  RrDatePicker,
  RrTag,
  type TableColumnType
} from '@xjlv2/admin-layout'

defineProps<{
  canManage: boolean
}>()

const emit = defineEmits<{
  notice: [message: string]
  error: [message: string]
}>()

const loading = ref(false)

// —— 日志筛选与列表 ——
const filters = ref<LogFilter>({ action: '', tag: '', keyword: '', from: '', to: '' })
const logs = ref<ReviewLog[]>([])
const listPaged = usePaged(() => logs.value, 10)

const hasFilter = computed(() =>
  Boolean(filters.value.action || filters.value.tag || filters.value.keyword || filters.value.from || filters.value.to)
)

const actionOptions = computed(() => [
  { label: '全部动作', value: '' },
  ...REVIEW_ACTION_OPTIONS
])

const tagOptions = computed(() => [
  { label: '全部标签', value: '' },
  ...MACHINE_TAG_OPTIONS
])

const columns: TableColumnType<ReviewLog>[] = [
  { key: 'id', title: '日志ID', width: 100 },
  { key: 'module', title: '所属模块', width: 120 },
  { key: 'content', title: '内容', minWidth: 200 },
  { key: 'hitTerm', title: '命中词', width: 120 },
  { key: 'hitPolicy', title: '命中策略', width: 120 },
  { key: 'action', title: '处理动作', width: 100 },
  { key: 'machineTag', title: '机审标签', width: 140 },
  { key: 'submittedAt', title: '提交时间', width: 160 },
  { key: 'actions', title: '操作', width: 100, align: 'right', fixed: 'right' }
]

async function refreshLogs() {
  loading.value = true
  emit('error', '')
  try {
    logs.value = await listLogs({ ...filters.value })
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '日志加载失败')
  } finally {
    loading.value = false
  }
}

function resetFilters() {
  filters.value = { action: '', tag: '', keyword: '', from: '', to: '' }
  void refreshLogs()
}

// —— 日志详情 ——
const showDetail = ref(false)
const detailLoading = ref(false)
const detailLog = ref<ReviewLog | null>(null)
const detailSegments = computed(() =>
  detailLog.value ? highlightSegments(detailLog.value.content, detailLog.value.hitTerm) : []
)

async function openDetail(log: ReviewLog) {
  showDetail.value = true
  detailLoading.value = true
  try {
    detailLog.value = await getLog(log.id)
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '日志详情加载失败')
  } finally {
    detailLoading.value = false
  }
}

// —— 导出 CSV ——
async function exportLogs() {
  try {
    const rows = await listLogs({ ...filters.value })
    const header = '日志ID,所属模块,处理动作,机审标签,命中策略,命中词,提交人,所属租户,提交时间,审核时间,内容'
    const lines = rows.map((row) => [
      row.id,
      row.module,
      reviewActionLabel(row.action),
      machineTagLabel(row.machineTag),
      row.hitPolicy,
      row.hitTerm,
      row.submitter,
      row.tenant,
      row.submittedAt,
      row.reviewedAt,
      row.content
    ].join(','))
    const blob = new Blob([`\uFEFF${[header, ...lines].join('\n')}`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = '内容审核日志.csv'
    anchor.click()
    URL.revokeObjectURL(url)
    emit('notice', `已导出 ${rows.length} 条日志。`)
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '日志导出失败')
  }
}

// 处理动作 → 状态标签色调
function actionTone(action: ReviewAction): any {
  return { BLOCK: 'danger', PASS: 'success', HUMAN: 'warning' }[action] || 'neutral'
}

function mapMachineTagTone(tag: string): any {
  const t = machineTagTone(tag as any)
  const mapping: Record<string, string> = {
    red: 'danger',
    pink: 'danger',
    orange: 'warning',
    maroon: 'danger',
    purple: 'primary',
    blue: 'info',
    teal: 'success',
    gray: 'neutral',
    green: 'success'
  }
  return mapping[t] || 'neutral'
}

onMounted(() => {
  void refreshLogs()
})

defineExpose({ refresh: refreshLogs })
</script>

<template>
  <section class="panel span management-list content-review-page">
    <div class="panel-head">
      <div>
        <p class="eyebrow">Content Review · Logs</p>
        <h2>日志管理</h2>
        <small>查看文本内容审核的处理记录，可依据处理动作与机审标签检索</small>
      </div>
    </div>

    <AdminFilterBar :loading="loading" @submit="refreshLogs" @reset="resetFilters">
      <RrSelect v-model:value="filters.action" :options="actionOptions" placeholder="处理动作" aria-label="处理动作" />
      <RrSelect v-model:value="filters.tag" :options="tagOptions" placeholder="机审标签" aria-label="机审标签" />
      <RrInput v-model:value.trim="filters.keyword" placeholder="输入内容、模块或请求ID检索" @keyup.enter="refreshLogs" />

      <div class="content-review-range" style="display: flex; align-items: center; gap: 8px;">
        <RrDatePicker value-format="YYYY-MM-DD" :value="filters.from || null" @update:value="filters.from = $event ?? ''" placeholder="开始日期" />
        至
        <RrDatePicker value-format="YYYY-MM-DD" :value="filters.to || null" @update:value="filters.to = $event ?? ''" placeholder="结束日期" />
      </div>

      <template #actions>
        <RrButton type="primary" :disabled="loading" @click="refreshLogs"><Search :size="16" />查询</RrButton>
        <RrButton v-if="hasFilter" type="default" :disabled="loading" @click="resetFilters"><RotateCcw :size="16" />重置</RrButton>
        <RrButton type="default" :disabled="loading" @click="exportLogs"><Download :size="16" />导出</RrButton>
      </template>
    </AdminFilterBar>

    <AdminDataTable
      :columns="columns"
      :data-source="listPaged.paged.value"
      :loading="loading"
      :error="''"
      :current="listPaged.page.value"
      :page-size="listPaged.pageSize.value"
      :total="listPaged.total.value"
      @update:current="listPaged.setPage"
      @update:page-size="(size) => (listPaged.pageSize.value = size)"
      @refresh="refreshLogs"
    >
      <template #bodyCell="{ column, record }">
        <strong v-if="column.key === 'id'">{{ record.id }}</strong>
        <template v-else-if="column.key === 'content'">
          <span class="content-review-content-preview">
            <span v-for="(segment, index) in highlightSegments(record.content, record.hitTerm)" :key="index">
              <mark v-if="segment.hit" class="hit-highlight">{{ segment.text }}</mark>
              <span v-else>{{ segment.text }}</span>
            </span>
          </span>
        </template>
        <template v-else-if="column.key === 'hitTerm'">{{ record.hitTerm || '-' }}</template>
        <template v-else-if="column.key === 'action'">
          <AdminStatusTag
            :text="reviewActionLabel(record.action)"
            :tone="actionTone(record.action)"
          />
        </template>
        <template v-else-if="column.key === 'machineTag'">
          <RrTag :tone="mapMachineTagTone(record.machineTag)">{{ machineTagLabel(record.machineTag) }}</RrTag>
        </template>
        <span v-else-if="column.key === 'actions'" class="row-actions compact-actions">
          <RrButton type="default" size="small" :disabled="loading" @click="openDetail(record)">查看</RrButton>
        </span>
      </template>
    </AdminDataTable>

    <AdminDrawer
      v-model:open="showDetail"
      title="日志详情"
      description="Log Detail"
      width="560"
    >
      <template v-if="detailLog">
        <p v-if="detailLoading" class="empty-state">正在加载详情...</p>
        <template v-else>
          <h3>基本信息</h3>
          <div class="content-review-kv">
            <span>日志ID</span><strong>{{ detailLog.id }}</strong>
            <span>所属模块</span><strong>{{ detailLog.module }}</strong>
            <span>提交时间</span><strong>{{ detailLog.submittedAt }}</strong>
            <span>审核时间</span><strong>{{ detailLog.reviewedAt }}</strong>
            <span>提交人</span><strong>{{ detailLog.submitter }}</strong>
            <span>所属租户</span><strong>{{ detailLog.tenant }}</strong>
          </div>

          <h3>识别结果</h3>
          <div class="content-review-kv">
            <span>处理动作</span>
            <AdminStatusTag :text="reviewActionLabel(detailLog.action)" :tone="actionTone(detailLog.action)" />
            <span>机审标签</span>
            <RrTag :tone="mapMachineTagTone(detailLog.machineTag)">{{ machineTagLabel(detailLog.machineTag) }}</RrTag>
            <span>命中策略</span><strong>{{ detailLog.hitPolicy }}</strong>
            <span>命中词</span><strong>{{ detailLog.hitTerm || '无命中词' }}</strong>
            <span>命中位置</span><strong>{{ detailLog.hitPosition > 0 ? `第 ${detailLog.hitPosition} 个字符处` : '无命中' }}</strong>
            <span>审核服务</span><strong>文本内容安全</strong>
          </div>

          <h3>审核内容</h3>
          <div class="content-review-log-content">
            <span v-for="(segment, index) in detailSegments" :key="index">
              <mark v-if="segment.hit" class="hit-highlight">{{ segment.text }}</mark><span v-else>{{ segment.text }}</span>
            </span>
          </div>
          <div class="content-review-kv content-review-request">
            <span>请求ID</span><code>{{ detailLog.requestId }}</code>
          </div>
        </template>
      </template>
    </AdminDrawer>
  </section>
</template>
